import { P2Alert, P2Button, P2Checkbox, P2TextDescription, P2TextInput, P2TextLabel } from 'react-bootstrap-common-script';
import ApplicationCooLayout from '../../../layout/application-coo-layout';
import { Separator } from '../../../components/separator';
import { useNavigate } from 'react-router-dom';
import { SectionCard } from '../../../components/card';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { useEffect, useState } from 'react';
import { PolymerAxios } from '../../../utils/axios';
import { API_NotifyCoOwner } from '../../../utils/api';
import { PolymerCrypto } from '../../../utils/encryptor';
import { useGetApplicationDetailQuery, useGetCpfServiceNotifiedStatusQuery } from '../../../redux/services/getStageE';
import { Owner } from '../../../models/polymer/Owner.model';
import { useGetApplicationUuid } from '../../../hooks/useGetApplicationUuid';
import { updateFormData } from '../../../redux/slices/application-step-1-slice';

interface MailingRecord {
  name: string;
  uinfin: string;
  emailAddress: string;
  checkbox: boolean;
  isAlreadyNotified: boolean;
}

/**
 * FROM DB, check if the user has been notified before AND/OR if they have completed payment,
 * If not notified and payment not done, then allow sending of message,
 * If payment done or notified already, show disabled name.
 * */
export default function StageENotifyCoApplicant() {
  const navigate = useNavigate();
  const applicationId = useGetApplicationUuid();
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const applicationStep1 = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const incomingOwnersData = applicationStep1?.household?.incoming_owners ?? [];
  const dispatch = useAppDispatch();

  const {
    data: proposedOwnersNotifiedStatus,
    isLoading,
    refetch,
  } = useGetCpfServiceNotifiedStatusQuery({ application_id: applicationId ?? '0', uinfin: loginUser?.uinfin, singpass_token: singpassToken ?? '0' });

  // Use local state to manipulate
  const [otherOwnersList, setOtherOwnersList] = useState<MailingRecord[]>([]);
  const [successAlert, setSuccessAlert] = useState<boolean>(false);

  useEffect(() => {
    if (incomingOwnersData.length === 0 && !proposedOwnersNotifiedStatus) {
      return;
    }
    const filteredIncomingOwners = incomingOwnersData.filter((owner) => owner?.myinfo?.uinfin !== loginUser?.uinfin);
    setOtherOwnersList(
      filteredIncomingOwners.map((e: Owner) => {
        const findNotifiedStatus = proposedOwnersNotifiedStatus?.find(
          (item: { cpf_e_serviced_notified: boolean; uinfin: string }) => PolymerCrypto.decryption(item?.uinfin) === e?.myinfo?.uinfin
        );
        return {
          uinfin: e?.myinfo?.uinfin,
          emailAddress: e?.myinfo?.email ?? '',
          name: e?.myinfo?.name,
          checkbox: false,
          isAlreadyNotified: findNotifiedStatus?.cpf_e_serviced_notified,
        };
      })
    );
    console.log('incomingOwnersData', incomingOwnersData);
    console.log('filteredIncomingOwners', filteredIncomingOwners);
  }, [incomingOwnersData, proposedOwnersNotifiedStatus]);

  // Navigates back to back that brought you to this component
  const handleNavigateBack = () => {
    navigate(-1);
  };

  const handleSendMessage = async () => {
    const sendList = otherOwnersList.filter((o) => o.checkbox);
    if (sendList.length === 0) return;

    try {
      // 1) call API once per owner (parallel)
      const requests = sendList.map((owner) => {
        const payload = {
          uinfin: PolymerCrypto.encryption(owner.uinfin),
          name: owner.name,
          email: owner.emailAddress,
        };
        // return the axios promise directly — let it reject on network / non-2xx
        return PolymerAxios(owner.uinfin, singpassToken).post(`${API_NotifyCoOwner}/${applicationId}`, payload);
      });

      // If any request rejects, Promise.all will throw and go to catch -> all-or-nothing
      await Promise.all(requests);

      // 2) All succeeded -> update Redux with new emails from sendList
      const updatedFormData = {
        ...applicationStep1,
        household: {
          ...applicationStep1.household,
          incoming_owners: applicationStep1.household.incoming_owners.map((owner: any) => {
            const match = sendList.find((s) => s.uinfin === owner.myinfo?.uinfin);
            if (match) {
              return {
                ...owner,
                myinfo: {
                  ...owner.myinfo,
                  email: match.emailAddress,
                },
              };
            }
            return owner;
          }),
        },
      };

      dispatch(updateFormData(updatedFormData));

      // 3) cleanup / refresh UI
      reset(); // clear local checkboxes
      refetch(); // refresh notified status if needed
      setSuccessAlert(true);
      console.log(
        'All notifications succeeded:',
        sendList.map((s) => s.uinfin)
      );
    } catch (err) {
      // any single failure lands here -> no Redux update
      console.error('One or more notifications failed:', err);
      alert('An error occurred while sending the email. Please try again later.');
    }
  };

  const handleCheckbox = (ownerDetail: MailingRecord) => {
    // Flip the boolean of the checkbox if uinfin matches
    setOtherOwnersList((prevData: MailingRecord[]) => {
      return prevData.map((each: MailingRecord) => {
        if (each?.uinfin === ownerDetail?.uinfin) {
          return { ...each, checkbox: !each.checkbox };
        }
        return each; // Return the original object if id doesn't match
      });
    });
  };

  const reset = () => {
    setOtherOwnersList((prevData: MailingRecord[]) => {
      return prevData.map((each: MailingRecord) => {
        return { ...each, checkbox: false }; // Set checkbox to false for all owners
      });
    });
  };

  const handleEmailChange = (incomingOwner: MailingRecord, e: string) => {
    // Update email state by matching uinfin
    setOtherOwnersList((prevData: MailingRecord[]) => {
      return prevData.map((each: MailingRecord) => {
        if (each?.uinfin === incomingOwner?.uinfin) {
          return { ...each, emailAddress: e };
        }
        return each;
      });
    });
  };

  return (
    <ApplicationCooLayout title={'Apply to Withdraw CPF Funds Due to Change in Flat Ownership'}>
      <div className='polymer-application-coo__wrapper cpf-wrapper'>
        <div className='cpf-wrapper-inner'>
          {successAlert && (
            <div className='w-100'>
              <P2Alert withBorder variant='success' text='Email has been sent to the selected co-owner(s).' />
            </div>
          )}

          <SectionCard
            required={false}
            className='flex-fill'
            titleChildren={<P2TextDescription title={'Notify Co-Owner'} />}
            contentChildren={
              <div className='polymer-stage-e'>
                <div className='cpf-container'>
                  <div>
                    <P2TextLabel
                      bold={700}
                      label={
                        'If your co-owner(s) plan to use their CPF savings to pay for the flat, you may notify them to submit the CPF withdrawal form. Please provide their email address'
                      }
                      fontSize={'1.5rem'}
                      lineHeight={'28px'}
                    />
                    <P2TextDescription
                      color='#333'
                      description_margin='0.575rem 0 0 0'
                      description={'Please select the co-owner whom you wish to notify, and key in the email address:'}
                      fontSize={'1rem'}
                      lineHeight={'1rem'}
                    />
                  </div>
                  <div className='polymer-stage-e_notify-co-applicant'>
                    {otherOwnersList.map((coOwner: MailingRecord, _index: number) => (
                      <div key={_index} className='polymer-stage-e_notify-co-applicant_item'>
                        <div className='name-title-content'>
                          <div className='name-title-content__title'>
                            <P2TextLabel bold={700} fontSize={'1.125rem'} lineHeight={'1.5rem'} label={'Name of Co-Owner'} />
                          </div>
                          <div className='checkbox-name'>
                            <div
                              className={`${
                                coOwner?.isAlreadyNotified ? 'polymer-stage-e_checkbox-wrapper_disabled' : 'polymer-stage-e_checkbox-wrapper-old'
                              }`}>
                              <P2Checkbox
                                variant={coOwner?.isAlreadyNotified ? 'disabled' : 'default'}
                                isActive={coOwner?.isAlreadyNotified ? true : coOwner?.checkbox}
                                onClick={() => handleCheckbox(coOwner)}
                              />
                            </div>
                            <P2TextLabel bold={400} fontSize={'1.125rem'} lineHeight={'1.5rem'} label={coOwner?.name} />
                          </div>
                        </div>

                        <div className='email-title-content'>
                          <P2TextLabel bold={700} fontSize={'1.125rem'} lineHeight={'1.5rem'} label={'Email Address'} />
                          <div className='polymer-stage-e_notify-co-applicant_email'>
                            <P2TextInput
                              disabled={coOwner?.isAlreadyNotified ? true : false}
                              value={coOwner?.emailAddress || ''}
                              onChange={(e: string) => handleEmailChange(coOwner, e)}
                              type='text'
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            }
          />
        </div>

        <div className='cpf-wrapper_button-wrapper'>
          <div>
            <Separator />
          </div>
          <div className='cpf-wrapper_button-group'>
            <P2Button onClick={() => handleNavigateBack()} variant={'outline-primary'} style={{ fontSize: '1rem' }}>
              Back
            </P2Button>
            <P2Button
              disabled={otherOwnersList.filter((otherOwner) => otherOwner?.checkbox === true).length > 0 ? false : true}
              onClick={() => handleSendMessage()}
              variant={'primary-1'}
              style={{ fontSize: '1rem' }}>
              Send
            </P2Button>
          </div>
        </div>
      </div>
    </ApplicationCooLayout>
  );
}
